import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  DocumentCheckIcon,
  ChatBubbleLeftIcon,
  PencilIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AnnotationCanvas from '../components/annotation/AnnotationCanvas'
import MessageModal from '../components/messaging/modals/MessageModal'
import ProjectNavigation from '../components/projects/ProjectNavigation'
import { projectAPI, imageAPI, annotationAPI, messageAPI } from '../services/api'
import { Project, ImageData, ImageWithAnnotations, ProjectStatus, COCOAnnotation } from '../types'

// Color palette for different annotators
const ANNOTATOR_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  // '#ef4444', // red - Reserved for MERGED annotations
]

interface UserAnnotationReview {
  user_id: string
  user_name: string
  user_email: string
  status: ProjectStatus
  has_annotations: boolean
  annotation_count: number
  last_updated: string
}

export default function AnnotationReviewPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [project, setProject] = useState<Project | null>(null)
  const [images, setImages] = useState<ImageData[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imageWithAnnotations, setImageWithAnnotations] = useState<ImageWithAnnotations | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [imageError, setImageError] = useState<string | null>(null)
  const [selectedAnnotators, setSelectedAnnotators] = useState<string[]>([])
  const [showMerged, setShowMerged] = useState(false)
  const [keypointRadius, setKeypointRadius] = useState(5)
  const [rectangleWidth, setRectangleWidth] = useState(3)
  const [currentViewSettings, setCurrentViewSettings] = useState<{
    scale?: number
    offset?: { x: number; y: number }
    keypointRadius?: number
    rectangleWidth?: number
  } | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(() => {
    const savedState = localStorage.getItem('annotation-fullscreen')
    return savedState === 'true'
  })
  const [shouldAutoFit, setShouldAutoFit] = useState(false)
  const [userReviews, setUserReviews] = useState<UserAnnotationReview[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [processingAction, setProcessingAction] = useState<string | null>(null)
  const [messageModalOpen, setMessageModalOpen] = useState(false)
  const [messageRecipient, setMessageRecipient] = useState<{
    id: string
    name: string
    email: string
  } | null>(null)
  const [userUnreadCounts, setUserUnreadCounts] = useState<Record<string, number>>({})

  const frameScrollRef = useRef<HTMLDivElement>(null)
  const hasInitializedSelection = useRef(false)

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('annotation-fullscreen', isFullscreen.toString())
  }, [isFullscreen])

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev)
  }, [])

  const handleViewSettingsChange = useCallback(
    (settings: {
      scale?: number
      offset?: { x: number; y: number }
      showGrid?: boolean
      autoAIEnabled?: boolean
      keypointRadius?: number
      rectangleWidth?: number
      showOnlySelected?: boolean
      currentTool?: 'select' | 'pan'
    }) => {
      // Extract settings for localStorage (tool settings + showGrid)
      const { showGrid, ...imageSettings } = settings

      // Save settings to localStorage
      if (showGrid !== undefined) {
        localStorage.setItem('annotation-showGrid', showGrid.toString())
      }

      // Update local state
      if (settings.keypointRadius !== undefined && settings.keypointRadius !== keypointRadius) {
        setKeypointRadius(settings.keypointRadius)
      }
      if (settings.rectangleWidth !== undefined && settings.rectangleWidth !== rectangleWidth) {
        setRectangleWidth(settings.rectangleWidth)
      }

      // Update current view settings
      setCurrentViewSettings(prev => ({
        ...prev,
        scale: imageSettings.scale,
        offset: imageSettings.offset,
        keypointRadius: settings.keypointRadius ?? keypointRadius,
        rectangleWidth: settings.rectangleWidth ?? rectangleWidth,
      }))
    },
    [keypointRadius, rectangleWidth]
  )

  const sortedUserReviews = useMemo(() => {
    return [...userReviews].sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''))
  }, [userReviews])

  const getUserColor = useCallback(
    (userId: string) => {
      const index = sortedUserReviews.findIndex(u => u.user_id === userId)
      if (index !== -1) {
        return ANNOTATOR_COLORS[index % ANNOTATOR_COLORS.length]
      }
      // Fallback hash
      let hash = 0
      for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash)
      }
      return ANNOTATOR_COLORS[Math.abs(hash) % ANNOTATOR_COLORS.length]
    },
    [sortedUserReviews]
  )

  const displayData = useMemo(() => {
    if (!imageWithAnnotations) return { annotations: [], colors: {} }

    const annotations: COCOAnnotation[] = []
    const colors: Record<string, string> = {}

    // Add individual user annotations
    imageWithAnnotations.annotations.forEach(userAnno => {
      if (selectedAnnotators.includes(userAnno.user_id)) {
        const color = getUserColor(userAnno.user_id)

        userAnno.annotations.forEach(anno => {
          // Create a unique ID combining user ID and annotation ID to prevent collision
          const uniqueId = `${userAnno.user_id}-${anno.id}`

          annotations.push({
            ...anno,
            id: uniqueId,
          })
          colors[uniqueId] = color
        })
      }
    })

    // Add merged annotations
    if (showMerged && imageWithAnnotations.merged_annotations) {
      imageWithAnnotations.merged_annotations.forEach((merged, idx) => {
        const id = `merged-${idx}`
        const numKeypoints = merged.keypoints.filter((_, i) => i % 3 === 2 && _ > 0).length

        const anno: COCOAnnotation = {
          id,
          category_id: 0,
          bbox: merged.bbox as [number, number, number, number],
          keypoints: merged.keypoints,
          num_keypoints: numKeypoints,
        }
        annotations.push(anno)
        colors[id] = '#ef4444' // Red for merged - make sure this is distinct from user colors
      })
    }

    return { annotations, colors }
  }, [imageWithAnnotations, selectedAnnotators, showMerged, getUserColor])

  useEffect(() => {
    if (projectId) {
      loadProjectData()
    }
  }, [projectId])

  useEffect(() => {
    if (images.length > 0) {
      fetchImageAnnotations(images[currentImageIndex].id)
      fetchUserReviews()
      fetchUserUnreadCounts()
    }
  }, [currentImageIndex, images])

  const loadProjectData = async () => {
    if (!projectId) {
      alert('Project ID is missing')
      navigate('/projects')
      return
    }

    try {
      setLoading(true)
      const [projectRes, imagesRes] = await Promise.all([
        projectAPI.get(projectId),
        imageAPI.listAll(projectId),
      ])
      setProject(projectRes.data)
      setImages(imagesRes.data)
    } catch (err) {
      console.error('Error loading project:', err)
      alert('Failed to load project')
    } finally {
      setLoading(false)
    }
  }

  const fetchImageAnnotations = async (imageId: string) => {
    if (!projectId) return

    try {
      setLoading(true)
      setImageError(null)
      const response = await annotationAPI.getAllImageAnnotations(projectId, imageId)
      setImageWithAnnotations(response.data)

      // Only auto-select annotators if we haven't initialized selection yet
      if (!hasInitializedSelection.current && response.data.annotations.length > 0) {
        const annotatorIds = [
          ...new Set(response.data.annotations.map((a: any) => a.user_id)),
        ] as string[]
        setSelectedAnnotators(annotatorIds)
        hasInitializedSelection.current = true
      }
    } catch (error) {
      console.error('Error fetching image annotations:', error)
      setImageError('Failed to load image annotations')
      setImageWithAnnotations(null)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserReviews = async () => {
    if (!projectId) return

    try {
      // This endpoint needs to be implemented to get review status for each user
      const response = await annotationAPI.getUserAnnotationStatuses(projectId)
      setUserReviews(response.data)
    } catch (error) {
      console.error('Error fetching user reviews:', error)
    }
  }

  const fetchUserUnreadCounts = async () => {
    if (!projectId) return

    try {
      const response = await messageAPI.getUnreadCounts({ projectId, includeUserBreakdown: true })
      setUserUnreadCounts(response.data.user_unread_counts || {})
    } catch (error) {
      console.error('Error fetching user unread counts:', error)
      setUserUnreadCounts({})
    }
  }

  const handleApproveUser = async (userId: string) => {
    if (!projectId || !confirm(`Approve annotations from this user for this project?`)) return

    try {
      setProcessingAction(userId)
      await annotationAPI.approveUserAnnotations(projectId, userId)
      await fetchUserReviews()
      // Reload current image annotations to update merged_annotations
      if (currentImage) {
        await fetchImageAnnotations(currentImage.id)
      }
    } catch (error) {
      console.error('Error approving annotations:', error)
      alert('Failed to approve annotations')
    } finally {
      setProcessingAction(null)
    }
  }

  const handleRejectUser = async (userId: string) => {
    if (!projectId) return

    const reason = prompt('Please provide a reason for revision:')
    if (!reason) return

    try {
      setProcessingAction(userId)
      await annotationAPI.rejectUserAnnotations(projectId, userId, { reason })
      await fetchUserReviews()
      // Reload current image annotations to update merged_annotations
      if (currentImage) {
        await fetchImageAnnotations(currentImage.id)
      }
    } catch (error) {
      console.error('Error rejecting annotations:', error)
      alert('Failed to mark annotations for revision')
    } finally {
      setProcessingAction(null)
    }
  }

  const handleOpenMessage = (userId: string, userName: string, userEmail: string) => {
    setMessageRecipient({ id: userId, name: userName, email: userEmail })
    setMessageModalOpen(true)
  }

  const handleCloseMessage = () => {
    setMessageModalOpen(false)
    setMessageRecipient(null)
    // Refresh unread counts after closing message modal
    fetchUserUnreadCounts()
  }

  const toggleAnnotator = (annotatorId: string) => {
    hasInitializedSelection.current = true
    if (annotatorId === 'merged') {
      setShowMerged(!showMerged)
    } else {
      setSelectedAnnotators(prev =>
        prev.includes(annotatorId) ? prev.filter(id => id !== annotatorId) : [...prev, annotatorId]
      )
    }
  }

  const selectSingleAnnotator = (annotatorId: string) => {
    hasInitializedSelection.current = true
    setShowMerged(false)
    setSelectedAnnotators([annotatorId])
  }

  const selectOnlyMerged = () => {
    hasInitializedSelection.current = true
    setShowMerged(true)
    setSelectedAnnotators([])
  }

  const navigateImage = (newIndex: number) => {
    setCurrentImageIndex(newIndex)
  }

  const handlePrevious = () => {
    if (currentImageIndex > 0) {
      navigateImage(currentImageIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentImageIndex < images.length - 1) {
      navigateImage(currentImageIndex + 1)
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentImageIndex > 0) {
        navigateImage(currentImageIndex - 1)
      } else if (e.key === 'ArrowRight' && currentImageIndex < images.length - 1) {
        navigateImage(currentImageIndex + 1)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [currentImageIndex, images.length])

  if (loading && !imageWithAnnotations) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading review tool...</p>
      </div>
    )
  }

  if (!project || images.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">No images to review</p>
        <button className="btn-primary" onClick={() => navigate('/projects')}>
          Back to Projects
        </button>
      </div>
    )
  }

  const currentImage = images[currentImageIndex]

  return (
    <div
      className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-screen'} flex flex-col bg-gray-100`}
    >
      {/* Header */}
      <div
        className={`bg-white border-b border-gray-200 px-4 ${isFullscreen ? 'py-2' : 'pt-4 pb-3'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {!isFullscreen ? (
              <>
                <button
                  onClick={() => navigate('/projects')}
                  className="mr-4 text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold">{project.name} - Annotation Review</h1>
                  <p className="text-sm text-gray-600">
                    Image {currentImageIndex + 1} of {images.length} - {currentImage.filename}
                  </p>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/projects')}
                  className="mr-3 text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
                <div className="flex items-center">
                  <h1 className="text-base font-semibold">{project.name} - Review</h1>
                  <span className="ml-2 text-sm text-gray-600">
                    - Image {currentImageIndex + 1}/{images.length} - {currentImage.filename}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className={`flex items-center ${isFullscreen ? 'gap-3' : 'gap-4'}`}>
            <div className={`flex items-center gap-2 ${isFullscreen ? 'text-xs' : 'text-sm'}`}>
              <DocumentCheckIcon className={isFullscreen ? 'w-4 h-4' : 'w-5 h-5'} />
              <span className="text-gray-600">
                {userReviews.filter(u => u.status === ProjectStatus.QA_APPROVED).length} /{' '}
                {
                  userReviews.filter(
                    u =>
                      u.status === ProjectStatus.NEEDS_REVISION ||
                      u.status === ProjectStatus.QA_PENDING ||
                      u.status === ProjectStatus.QA_APPROVED
                  ).length
                }{' '}
                users approved
              </span>
            </div>
            <ProjectNavigation projectId={projectId!} activePage="review" />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - User list and review actions */}
        <div className="w-64 bg-white border-r border-gray-200 p-4 flex flex-col overflow-hidden">
          {/* Review Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="space-y-1 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-600">Pending:</span>
                <span className="font-semibold text-gray-700">
                  {userReviews.filter(u => u.status === ProjectStatus.QA_PENDING).length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-600">Needs Revision:</span>
                <span className="font-semibold text-orange-700">
                  {userReviews.filter(u => u.status === ProjectStatus.NEEDS_REVISION).length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-600">Approved:</span>
                <span className="font-semibold text-green-700">
                  {userReviews.filter(u => u.status === ProjectStatus.QA_APPROVED).length}
                </span>
              </div>
            </div>
          </div>

          {/* Display Settings */}
          <div className="mb-4">
            <div className="space-y-4">
              {/* Keypoint Radius */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keypoint Radius
                </label>
                <input
                  type="number"
                  value={keypointRadius}
                  onChange={e => {
                    const newRadius = Math.max(2, Math.min(10, parseInt(e.target.value) || 6))
                    setKeypointRadius(newRadius)
                  }}
                  min="2"
                  max="10"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Rectangle Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rectangle Width
                </label>
                <input
                  type="number"
                  value={rectangleWidth}
                  onChange={e => {
                    const newWidth = Math.max(1, Math.min(10, parseInt(e.target.value) || 3))
                    setRectangleWidth(newWidth)
                  }}
                  min="1"
                  max="10"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-4"></div>

            {/* Show merged annotations toggle */}
            <div>
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    imageWithAnnotations?.merged_annotations &&
                    imageWithAnnotations.merged_annotations.length > 0
                      ? 'text-gray-700'
                      : 'text-gray-400'
                  }`}
                >
                  Show Merged
                </span>

                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      selectOnlyMerged()
                    }}
                    disabled={
                      !imageWithAnnotations?.merged_annotations ||
                      imageWithAnnotations.merged_annotations.length === 0
                    }
                    className={`p-1 rounded hover:bg-blue-50 ${
                      !imageWithAnnotations?.merged_annotations ||
                      imageWithAnnotations.merged_annotations.length === 0
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-blue-600'
                    }`}
                    title="Show only Merged"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <label
                    className={`flex items-center p-1 ${
                      imageWithAnnotations?.merged_annotations &&
                      imageWithAnnotations.merged_annotations.length > 0
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showMerged}
                      onChange={() => setShowMerged(!showMerged)}
                      disabled={
                        !imageWithAnnotations?.merged_annotations ||
                        imageWithAnnotations.merged_annotations.length === 0
                      }
                      className="rounded w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 mt-4"></div>
          </div>

          {/* User list with review status */}
          <div className="flex-1 overflow-y-auto space-y-2 no-scrollbar">
            {userReviews
              .filter(
                userReview =>
                  // Show users if they have the expected statuses OR if they have unread messages
                  userReview.status === ProjectStatus.NEEDS_REVISION ||
                  userReview.status === ProjectStatus.QA_PENDING ||
                  userReview.status === ProjectStatus.QA_APPROVED ||
                  userUnreadCounts[userReview.user_id] > 0
              )
              .map(userReview => {
                // Find annotation data for this user on current image
                const userAnnotation = imageWithAnnotations?.annotations.find(
                  anno => anno.user_id === userReview.user_id
                )
                const color = getUserColor(userReview.user_id)
                const isSelected = selectedUserId === userReview.user_id

                return (
                  <div
                    key={userReview.user_id}
                    onClick={() => setSelectedUserId(userReview.user_id)}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="mb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: color,
                            }}
                          />
                          <p
                            className="font-medium text-sm truncate"
                            title={userAnnotation?.user_name || userReview.user_name}
                          >
                            {userAnnotation?.user_name || userReview.user_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {/* Show only this user button */}
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              selectSingleAnnotator(userReview.user_id)
                            }}
                            className="text-gray-400 hover:text-blue-600 p-1 rounded hover:bg-blue-50"
                            title="Show only this user"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {/* Show checkbox for annotation visibility toggle */}
                          <label
                            className="flex items-center p-1 cursor-pointer"
                            onClick={e => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAnnotators.includes(userReview.user_id)}
                              onChange={() => toggleAnnotator(userReview.user_id)}
                              className={`rounded w-4 h-4 border-gray-300 focus:ring-blue-500 ${
                                userAnnotation ? 'text-blue-600' : 'text-gray-400'
                              }`}
                              title={
                                userAnnotation
                                  ? 'Toggle annotation visibility'
                                  : 'No annotations on this image'
                              }
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Annotation stats */}
                    <div className="text-xs text-gray-600 mb-2">
                      <div>{userAnnotation?.annotations?.length || 0} objects on this image</div>
                      <div className="mt-1">
                        {userAnnotation?.updated_at
                          ? `Last updated: ${new Date(userAnnotation.updated_at).toLocaleDateString()}`
                          : 'No annotations on this image'}
                      </div>
                      {userUnreadCounts[userReview.user_id] > 0 && (
                        <div className="mt-1 text-red-600">
                          {userUnreadCounts[userReview.user_id]} unread message
                          {userUnreadCounts[userReview.user_id] > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    {/* Review status */}
                    <div className="mb-2">
                      {userReview.status === ProjectStatus.QA_APPROVED ? (
                        <div className="flex items-center gap-1 text-green-600 text-xs">
                          <CheckCircleIconSolid className="w-4 h-4" />
                          <span>Approved</span>
                        </div>
                      ) : userReview.status === ProjectStatus.NEEDS_REVISION ? (
                        <div className="flex items-center gap-1 text-orange-600 text-xs">
                          <ExclamationTriangleIcon className="w-4 h-4" />
                          <span>Needs Revision</span>
                        </div>
                      ) : userReview.status === ProjectStatus.DRAFT ? (
                        <div className="flex items-center gap-1 text-blue-600 text-xs">
                          <PencilIcon className="w-4 h-4" />
                          <span>Draft</span>
                        </div>
                      ) : userReview.status === ProjectStatus.QA_PENDING ? (
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <UserIcon className="w-4 h-4" />
                          <span>Pending Review</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-400 text-xs">
                          <UserIcon className="w-4 h-4" />
                          <span>Not Started</span>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    {isSelected && (
                      <div className="space-y-2 mt-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveUser(userReview.user_id)}
                            disabled={processingAction === userReview.user_id}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                          >
                            <CheckCircleIcon className="w-3 h-3" />
                            <span>Approve</span>
                          </button>
                          <button
                            onClick={() => handleRejectUser(userReview.user_id)}
                            disabled={processingAction === userReview.user_id}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 disabled:opacity-50"
                          >
                            <XCircleIcon className="w-3 h-3" />
                            <span>Revision</span>
                          </button>
                        </div>
                        <button
                          onClick={() =>
                            handleOpenMessage(
                              userReview.user_id,
                              userAnnotation?.user_name || userReview.user_name,
                              userAnnotation?.user_email || userReview.user_email
                            )
                          }
                          className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          <ChatBubbleLeftIcon className="w-3 h-3" />
                          <span>Message</span>
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </div>

        {/* Center - Annotation Canvas */}
        <div className="flex-1 min-w-0 flex">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : imageError ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <p className="text-red-500 mb-2">{imageError}</p>
              <button
                onClick={() => fetchImageAnnotations(images[currentImageIndex].id)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : imageWithAnnotations ? (
            <AnnotationCanvas
              image={currentImage}
              project={project}
              annotations={displayData.annotations}
              currentKeypoint={0}
              selectedAnnotationId={null}
              onAnnotationSelect={() => {}}
              onAnnotationAdd={() => {}}
              onAnnotationUpdate={() => {}}
              onAnnotationDelete={() => {}}
              onKeypointUpdate={() => {}}
              viewSettings={currentViewSettings || undefined}
              onViewSettingsChange={handleViewSettingsChange}
              keypointRadius={keypointRadius}
              rectangleWidth={rectangleWidth}
              isFullscreen={isFullscreen}
              onToggleFullscreen={toggleFullscreen}
              shouldAutoFit={shouldAutoFit}
              onAutoFitComplete={() => setShouldAutoFit(false)}
              readOnly={true}
              annotationColors={displayData.colors}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500">No annotations available for this image</p>
            </div>
          )}
        </div>

        {/* Right sidebar - Image navigation */}
        <div className="w-56 bg-white border-l border-gray-200 p-4 flex flex-col overflow-hidden">
          {/* Navigation controls */}
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <button
                onClick={handlePrevious}
                disabled={currentImageIndex === 0}
                className="btn-secondary flex items-center text-sm py-1 px-3"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                Previous
              </button>

              <button
                onClick={handleNext}
                disabled={currentImageIndex === images.length - 1}
                className="btn-secondary flex items-center text-sm py-1 px-3"
              >
                Next
                <ChevronRightIcon className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>

          {/* Image list */}
          <div ref={frameScrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
            <div className="space-y-2">
              {images.map((img, index) => {
                const isCurrentImage = index === currentImageIndex
                // Check if any user has annotations on this image
                const hasAnnotations =
                  isCurrentImage &&
                  imageWithAnnotations?.annotations &&
                  imageWithAnnotations.annotations.length > 0

                return (
                  <button
                    key={img.id}
                    onClick={() => navigateImage(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      isCurrentImage
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {img.meta_data?.source === 'video_extraction'
                            ? `Frame ${img.frame_number || index + 1}`
                            : `Image ${index + 1}`}
                        </p>
                      </div>
                      {hasAnnotations ? (
                        <CheckCircleIconSolid className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      {messageRecipient && (
        <MessageModal
          isOpen={messageModalOpen}
          onClose={handleCloseMessage}
          projectId={projectId!}
          recipientId={messageRecipient.id}
          recipientName={messageRecipient.name}
          recipientEmail={messageRecipient.email}
        />
      )}
    </div>
  )
}
